/**
 * Optional biometric enrollment component shown after PIN setup.
 *
 * Offers the user the choice to enable biometric (Face ID / fingerprint)
 * unlock for convenience. PIN is always available as fallback.
 * If WebAuthn is not available on the device, auto-skips.
 *
 * Tier: All tiers (core infrastructure, onboarding step).
 *
 * @module BiometricSetup
 */

import { useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useBiometric } from '@/features/auth/hooks/useBiometric';

/** Props for BiometricSetup component. */
interface BiometricSetupProps {
  /** The PIN just set by the user, needed to store encrypted for biometric unlock. */
  readonly pin: string;
  /** Called when biometric enrollment completes (enabled or error-skipped). */
  readonly onComplete: () => void;
  /** Called when the user explicitly skips biometric setup. */
  readonly onSkip: () => void;
}

/**
 * Biometric enrollment UI shown after PIN creation during onboarding.
 *
 * Checks device capability via useBiometric hook. If biometric is not
 * available, auto-skips via onSkip. Otherwise shows enable/skip buttons.
 *
 * Tier: All tiers (core infrastructure).
 */
export function BiometricSetup({
  pin,
  onComplete,
  onSkip,
}: BiometricSetupProps): React.ReactElement {
  const { isAvailable, isLoading, error, enable, clearError } = useBiometric();

  // Auto-skip if biometric is not available on this device.
  // useBiometric sets isAvailable on mount via useEffect; we watch it here.
  // We skip only when isAvailable is definitively false (not just initial false).
  // The hook starts with isAvailable=false and updates it async. We use a
  // data-driven approach: if the hook reports not available after its check,
  // the component simply won't show the enable button and the parent should
  // handle the skip. For onboarding, we auto-skip.
  useEffect(() => {
    // isAvailable is set by useBiometric after its async check.
    // We can't distinguish "not yet checked" from "checked and false" here,
    // so we rely on a small delay to let the check complete.
    // Using requestAnimationFrame ensures we wait at least one render cycle.
    let cancelled = false;
    const id = requestAnimationFrame(() => {
      if (!cancelled && !isAvailable) {
        onSkip();
      }
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(id);
    };
  }, [isAvailable, onSkip]);

  const handleEnable = useCallback(async (): Promise<void> => {
    clearError();
    try {
      await enable(pin);
      onComplete();
    } catch {
      // If biometric enrollment fails, skip gracefully
      onSkip();
    }
  }, [pin, enable, onComplete, onSkip, clearError]);

  // If not available, render nothing (auto-skip will fire)
  if (!isAvailable) {
    return (
      <main className="flex min-h-dvh items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div
            className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary"
            aria-hidden="true"
          />
          <p className="text-sm text-muted-foreground" role="status" aria-live="polite">
            Checking biometric support...
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-dvh items-center justify-center bg-background px-6">
      <div className="flex w-full max-w-sm flex-col items-center gap-8">
        {/* Fingerprint icon */}
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-primary"
            aria-hidden="true"
          >
            <path d="M12 10a2 2 0 0 0-2 2c0 1.02-.1 2.51-.26 4" />
            <path d="M14 13.12c0 2.38 0 6.38-1 8.88" />
            <path d="M17.29 21.02c.12-.6.43-2.3.5-3.02" />
            <path d="M2 12a10 10 0 0 1 18-6" />
            <path d="M2 16h.01" />
            <path d="M21.8 16c.2-2 .131-5.354 0-6" />
            <path d="M5 19.5C5.5 18 6 15 6 12a6 6 0 0 1 .34-2" />
            <path d="M8.65 22c.21-.66.45-1.32.57-2" />
            <path d="M9 6.8a6 6 0 0 1 9 5.2v2" />
          </svg>
        </div>

        <div className="flex flex-col items-center gap-2 text-center">
          <h2 className="text-xl font-semibold text-foreground">Use biometric unlock?</h2>
          <p className="text-sm text-muted-foreground">
            Unlock your wallet quickly with Face ID or fingerprint. You can always use your PIN
            instead.
          </p>
        </div>

        {error ? (
          <p className="text-sm text-destructive text-center" role="alert" aria-live="assertive">
            {error}
          </p>
        ) : null}

        <div className="flex w-full flex-col gap-3">
          <Button onClick={() => void handleEnable()} loading={isLoading} size="lg">
            Enable biometric
          </Button>
          <Button variant="ghost" onClick={onSkip} disabled={isLoading} size="lg">
            Skip for now
          </Button>
        </div>
      </div>
    </main>
  );
}

export type { BiometricSetupProps };
